import type { TestStatusView } from "@shared/types/notifications";
import { useL1TeamsTestStatus, useSendL1TeamsTest } from "@/hooks/use-notifications";
import type { ApiClientError } from "@/lib/api";
import {
  L1_TEAMS_TEST_WAITING_TOO_LONG,
  l1TeamsTestRateLimitedSentence,
  l1TeamsTestResultSentence,
  retryAtOf,
} from "@/lib/notifications/sentences";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { PlatformTestDialog } from "./PlatformTestDialog";

/**
 * #2165: "Send a test" on the L1 Teams channel card. It posts a fixed test card to the L1
 * channel only, through the same path as every L1 alert, and says the result inline: sent, or
 * the failure's fixed sentence with who fixes it, never "Still waiting" after a delivery ended.
 */
export function SendL1TeamsTestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const zone = useDisplayTz();
  const send = useSendL1TeamsTest();

  function refusal(error: ApiClientError): string | null {
    if (error.status === 429) return l1TeamsTestRateLimitedSentence(retryAtOf(error.details), zone);
    return null;
  }

  return (
    <PlatformTestDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Send a test to the L1 Teams channel"
      testIdRoot="send-l1-teams-test"
      intro="A test card is posted to the L1 Teams channel. Nobody else receives it."
      channel="teams"
      send={send}
      useStatus={useL1TeamsTestStatus}
      resultSentence={(answer: TestStatusView) => l1TeamsTestResultSentence(answer, zone)}
      waitingTooLong={L1_TEAMS_TEST_WAITING_TOO_LONG}
      refusalSentence={refusal}
      queueFailure="The test could not be queued."
      statusFailure="The test's status did not load."
    />
  );
}
